import React from 'react';

import { ProviderPortalFeatureFlagKey } from './providerPortalFeatureFlags';
import { useFeatureFlag } from './useFeatureFlag';

export type FeatureFlagSwitchProps = {
    flag: ProviderPortalFeatureFlagKey;
    children: React.ReactNode;
    fallback?: React.ReactNode;
    /**
     * Set to {@code true} when the flag's name is phrased as a negative (e.g.
     * `..._DISABLED`), so `children` renders when the flag is *disabled* rather than enabled.
     */
    negate?: boolean;
};

/**
 * Renders {@link FeatureFlagSwitchProps.children} when `flag` is enabled, otherwise renders
 * {@link FeatureFlagSwitchProps.fallback} (or nothing if no fallback is given). Pass
 * `negate` to invert this when the flag name itself is a negative condition.
 *
 * Reusable across applications so call sites don't need to call {@link useFeatureFlag} directly:
 *
 * ```tsx
 * <FeatureFlagSwitch flag={ProviderPortalFeatureFlag.BHPNPP_3077_IS_TWO_WAY_MESSAGING_DISABLED_IS_TWO_WAY_MESSAGING_ENABLED} fallback={<OldWidget />}>
 *     <NewWidget />
 * </FeatureFlagSwitch>
 * ```
 */
export const FeatureFlagSwitch: React.FC<FeatureFlagSwitchProps> = ({ flag, children, fallback = null, negate = false }) => {
    const flagValue = useFeatureFlag(flag);
    const shouldRenderChildren = negate ? !flagValue : flagValue;
    return <>{shouldRenderChildren ? children : fallback}</>;
};
